/**
 * Utility functions for ContactList operations
 */

import type { Contact, ContactList } from './types';
import { ContactLogic } from '@/modules/contact';
import type {
  ContactIndex,
  DuplicateContact,
  DuplicateContactGroup,
} from './reducer';

/**
 * Convert array of contacts to vCard 4.0 format string
 */
export function contactsToVCards(contacts: ContactList): string {
  return contacts
    .map(contact => ContactLogic.toVCard(contact))
    .join('\r\n\r\n');
}

/**
 * Parse vCard text to array of Contact objects
 */
export function contactsFromVCards(vcardText: string): Contact[] {
  try {
    // Split by BEGIN:VCARD to get individual vCard entries
    const vcardEntries = vcardText.split(/BEGIN:VCARD/i);
    const contacts: Contact[] = [];

    for (const entry of vcardEntries) {
      if (!entry.trim()) continue;
      // Reconstruct the vCard entry
      const fullEntry = 'BEGIN:VCARD\n' + entry.trim();
      const contact = ContactLogic.fromVCard(fullEntry);
      if (contact) {
        contacts.push(contact);
      }
    }

    return contacts;
  } catch {
    return [];
  }
}

/**
 * Parse vCard file to array of Contact objects
 */
export async function parseVCardFile(file: File): Promise<Contact[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const vcardText = e.target?.result as string;
        const contacts = contactsFromVCards(vcardText);
        resolve(contacts);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsText(file);
  });
}

/**
 * Index a ContactList into a byId map + allIds array
 */
export function indexContacts(
  contacts: ContactList
): Pick<ContactIndex, 'byId' | 'allIds'> {
  const byId: Record<string, Contact> = {};
  const allIds: string[] = [];
  for (const contact of contacts) {
    byId[contact.localId] = contact;
    allIds.push(contact.localId);
  }
  return { byId, allIds };
}

/**
 * Look up all contacts from an indexed store (allIds contacts only)
 */
export function lookupAllContacts(state: ContactIndex): ContactList {
  return state.allIds.map(id => state.byId[id]).filter(Boolean);
}

/**
 * Serialize duplicate groups awaiting the Merge duplicates prompt, each
 * contact in the same JSON form the address book is stored in
 */
export function duplicatesToJSON(groups: DuplicateContactGroup[]): unknown[] {
  return groups.map(group =>
    group.map(dup => ({ ...dup, contact: ContactLogic.toJSON(dup.contact) }))
  );
}

/**
 * Deserialize duplicate groups, dropping any entry whose contact no longer
 * parses and any group left with fewer than two entries to compare
 */
export function duplicatesFromJSON(json: string): DuplicateContactGroup[] {
  try {
    const data: unknown = JSON.parse(json);
    if (!Array.isArray(data)) return [];
    return data
      .filter(Array.isArray)
      .map(group =>
        (
          group as Array<
            Omit<DuplicateContact, 'contact'> & { contact: unknown }
          >
        )
          .map(dup => {
            const contact = ContactLogic.fromJSON(dup.contact);
            return contact ? { ...dup, contact } : null;
          })
          .filter((dup): dup is DuplicateContact => dup !== null)
      )
      .filter(group => group.length > 1);
  } catch {
    return [];
  }
}

/**
 * Serialize contacts to JSON format
 */
export function contactsToJSON(
  contacts: ContactList
): ReturnType<typeof ContactLogic.toJSON>[] {
  return contacts.map(contact => ContactLogic.toJSON(contact));
}

/**
 * Deserialize contacts from JSON format
 */
export function contactsFromJSON(json: string | unknown): ContactList {
  try {
    const data = typeof json === 'string' ? JSON.parse(json) : json;
    if (!Array.isArray(data)) {
      return [];
    }
    const contacts = data
      .map(item => ContactLogic.fromJSON(item))
      .filter((contact): contact is Contact => contact !== null);
    return contacts;
  } catch {
    return [];
  }
}
